import { useState, useEffect, useRef } from "react";
const BASE = "https://imalishawelfare.com/api";
const tok = () => sessionStorage.getItem("token") || localStorage.getItem("adminToken") || localStorage.getItem("token");

const ACTIVITY_TYPES = {
  LOGIN_SUCCESS:   { label:"Login Success",    color:"#10b981", bg:"#f0fdf4",  icon:"✓" },
  LOGIN_FAILED:    { label:"Login Failed",      color:"#ef4444", bg:"#fee2e2",  icon:"✗" },
  LOAN_SELECTED:   { label:"Loan Selected",     color:"#0066cc", bg:"#eff6ff",  icon:"$" },
  STK_SENT:        { label:"STK Push Sent",     color:"#7c3aed", bg:"#f5f3ff",  icon:"M" },
  FEE_PAID:        { label:"Fee Paid",          color:"#10b981", bg:"#f0fdf4",  icon:"✓" },
  PAYMENT_FAILED:  { label:"Payment Failed",    color:"#ef4444", bg:"#fee2e2",  icon:"✗" },
  APPLIED:         { label:"New Application",   color:"#f59e0b", bg:"#fff8e6",  icon:"+" },
  APPROVED:        { label:"Loan Approved",     color:"#0052a3", bg:"#eff6ff",  icon:"A" },
  DISBURSED:       { label:"Loan Disbursed",    color:"#10b981", bg:"#f0fdf4",  icon:"D" },
  DASHBOARD_VIEW:  { label:"Dashboard Viewed",  color:"#64748b", bg:"#f8fafc",  icon:"i" },
};

const fmt = n => "KSh " + Number(n||0).toLocaleString("en-KE");
const fmtTime = d => new Date(d).toLocaleTimeString("en-KE", { hour:"2-digit", minute:"2-digit", second:"2-digit" });
const fmtDate = d => new Date(d).toLocaleDateString("en-KE", { day:"2-digit", month:"short" });

export default function ActivityMonitor() {
  const [activities, setActivities] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [filter, setFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const intervalRef = useRef(null);
  const tableRef = useRef(null);

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => { load(true); }, 10000);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [autoRefresh]);

  const load = async (silent=false) => {
    if (!silent) setLoading(true);
    try {
      const r = await fetch(`${BASE}/applications`, { headers:{ Authorization:"Bearer "+tok() } });
      if (r.ok) {
        const data = await r.json();
        setApps(data);
        // Generate activity feed from application states
        const acts = [];
        data.forEach(a => {
          // Application submitted
          if (a.createdAt) acts.push({ id:`app-${a.id}`, type:"APPLIED", time:a.createdAt, user:a.fullName, phone:a.phone, email:a.email, detail:`Applied for loan in ${a.county||"Kenya"}`, appId:a.id });
          // Login activity (approved apps have credentials)
          if (a.status === "APPROVED" || a.status === "DISBURSED") {
            if (a.loanStatus) {
              // They logged in since they have loan status
              acts.push({ id:`login-${a.id}`, type:"LOGIN_SUCCESS", time:a.createdAt, user:a.fullName, phone:a.phone, email:a.email, detail:`Beneficiary portal login`, appId:a.id });
            }
          }
          // Loan selected
          if (a.selectedLoanAmount && a.loanStatus) {
            acts.push({ id:`sel-${a.id}`, type:"LOAN_SELECTED", time:a.createdAt, user:a.fullName, phone:a.phone, email:a.email, detail:`Selected ${fmt(a.selectedLoanAmount)} loan`, amount:a.selectedLoanAmount, appId:a.id });
          }
          // STK sent (PENDING_FEE)
          if (a.loanStatus === "PENDING_FEE") {
            acts.push({ id:`stk-${a.id}`, type:"STK_SENT", time:a.createdAt, user:a.fullName, phone:a.phone, email:a.email, detail:`STK push sent for ${fmt(a.loanFeeAmount)} fee`, amount:a.loanFeeAmount, appId:a.id });
          }
          // Payment failed
          if (a.loanStatus === "PAYMENT_FAILED") {
            acts.push({ id:`fail-${a.id}`, type:"PAYMENT_FAILED", time:a.createdAt, user:a.fullName, phone:a.phone, email:a.email, detail:`M-Pesa payment failed`, appId:a.id });
          }
          // Fee paid
          if (a.loanStatus === "FEE_PAID" && a.mpesaReceiptNumber) {
            acts.push({ id:`paid-${a.id}`, type:"FEE_PAID", time:a.createdAt, user:a.fullName, phone:a.phone, email:a.email, detail:`Fee paid - Receipt: ${a.mpesaReceiptNumber}`, amount:a.loanFeeAmount, appId:a.id });
          }
          // Disbursed
          if (a.status === "DISBURSED" || a.loanStatus === "DISBURSED") {
            acts.push({ id:`disb-${a.id}`, type:"DISBURSED", time:a.createdAt, user:a.fullName, phone:a.phone, email:a.email, detail:`Loan of ${fmt(a.selectedLoanAmount)} disbursed`, amount:a.selectedLoanAmount, appId:a.id });
          }
          // Approved
          if (a.status === "APPROVED" || a.status === "DISBURSED") {
            acts.push({ id:`appr-${a.id}`, type:"APPROVED", time:a.createdAt, user:a.fullName, phone:a.phone, email:a.email, detail:`Application approved by admin`, appId:a.id });
          }
        });
        // Sort newest first
        acts.sort((a,b) => new Date(b.time) - new Date(a.time));
        setActivities(acts);
        setLastUpdate(new Date());
      }
    } catch(e) {}
    if (!silent) setLoading(false);
  };

  // Stats
  const stats = {
    totalLogins: activities.filter(a=>a.type==="LOGIN_SUCCESS").length,
    loginFails: activities.filter(a=>a.type==="LOGIN_FAILED").length,
    loanSelections: activities.filter(a=>a.type==="LOAN_SELECTED").length,
    stkSent: activities.filter(a=>a.type==="STK_SENT").length,
    paymentsFailed: activities.filter(a=>a.type==="PAYMENT_FAILED").length,
    feesPaid: activities.filter(a=>a.type==="FEE_PAID").length,
    disbursed: activities.filter(a=>a.type==="DISBURSED").length,
    newApps: activities.filter(a=>a.type==="APPLIED").length,
  };

  const filtered = activities.filter(a => {
    const matchFilter = filter === "ALL" || a.type === filter;
    const q = search.toLowerCase();
    const matchSearch = !q || a.user?.toLowerCase().includes(q) || a.phone?.includes(q) || a.email?.toLowerCase().includes(q) || a.detail?.toLowerCase().includes(q);
    return matchFilter && matchSearch;
  });

  const typeInfo = t => ACTIVITY_TYPES[t] || { label:t, color:"#94a3b8", bg:"#f8fafc", icon:"?" };

  return (
    <div style={{ fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif" }}>

      {/* Header */}
      <div style={{ background:"linear-gradient(135deg,#0f172a,#1e293b)", borderRadius:12, padding:"22px 28px", marginBottom:20 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:20, flexWrap:"wrap", gap:12 }}>
          <div>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:4 }}>
              <div style={{ width:8, height:8, background:autoRefresh?"#10b981":"#94a3b8", borderRadius:"50%", boxShadow:autoRefresh?"0 0 6px #10b981":"none", animation:autoRefresh?"pulse 2s infinite":"none" }}/>
              <h2 style={{ color:"white", fontSize:18, fontWeight:800, margin:0 }}>Live Activity Monitor</h2>
            </div>
            <p style={{ color:"rgba(255,255,255,0.5)", fontSize:12, margin:0 }}>
              Last updated: {fmtTime(lastUpdate)} · Auto-refresh every 10s
            </p>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={()=>setAutoRefresh(a=>!a)}
              style={{ padding:"8px 16px", background:autoRefresh?"#10b981":"#64748b", color:"white", border:"none", borderRadius:8, fontSize:12, fontWeight:600, cursor:"pointer" }}>
              {autoRefresh ? "Auto ON" : "Auto OFF"}
            </button>
            <button onClick={()=>load()}
              style={{ padding:"8px 16px", background:"rgba(255,255,255,0.1)", color:"white", border:"1px solid rgba(255,255,255,0.2)", borderRadius:8, fontSize:12, fontWeight:600, cursor:"pointer" }}>
              Refresh Now
            </button>
          </div>
        </div>

        {/* Stats grid */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(100px,1fr))", gap:8 }}>
          {[
            ["New Apps", stats.newApps, "#f59e0b"],
            ["Logins", stats.totalLogins, "#10b981"],
            ["Loan Selections", stats.loanSelections, "#0066cc"],
            ["STK Sent", stats.stkSent, "#7c3aed"],
            ["Fees Paid", stats.feesPaid, "#10b981"],
            ["Pay Failed", stats.paymentsFailed, "#ef4444"],
            ["Disbursed", stats.disbursed, "#10b981"],
          ].map(([label,val,color])=>(
            <div key={label} style={{ background:"rgba(255,255,255,0.08)", borderRadius:8, padding:"10px 12px", textAlign:"center" }}>
              <p style={{ color:"rgba(255,255,255,0.5)", fontSize:10, fontWeight:600, margin:"0 0 4px", textTransform:"uppercase", letterSpacing:.5 }}>{label}</p>
              <p style={{ color, fontSize:20, fontWeight:800, margin:0 }}>{val}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Filter tabs */}
      <div style={{ background:"white", borderRadius:10, padding:5, display:"flex", gap:3, marginBottom:16, boxShadow:"0 1px 4px rgba(0,0,0,0.06)", flexWrap:"wrap" }}>
        {[["ALL","All Events"], ...Object.entries(ACTIVITY_TYPES).map(([k,v])=>[k,v.label])].map(([key,label])=>(
          <button key={key} onClick={()=>setFilter(key)}
            style={{ padding:"7px 12px", border:"none", background:filter===key?"#0052a3":"transparent", color:filter===key?"white":"#64748b", borderRadius:7, fontWeight:filter===key?700:500, fontSize:11, cursor:"pointer", whiteSpace:"nowrap" }}>
            {label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div style={{ position:"relative", marginBottom:16 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)" }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search by name, phone, email..."
          style={{ width:"100%", padding:"10px 12px 10px 36px", border:"1px solid #e2e8f0", borderRadius:8, fontSize:13, boxSizing:"border-box", background:"white" }}/>
        <span style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", color:"#94a3b8", fontSize:12 }}>{filtered.length} events</span>
      </div>

      {/* Activity feed */}
      <div style={{ background:"white", borderRadius:12, boxShadow:"0 1px 6px rgba(0,0,0,0.06)", overflow:"hidden" }} ref={tableRef}>
        <div style={{ padding:"14px 20px", borderBottom:"1px solid #f1f5f9", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <h3 style={{ fontSize:14, fontWeight:700, color:"#1e293b", margin:0 }}>Activity Feed</h3>
          <span style={{ fontSize:12, color:"#94a3b8" }}>{filtered.length} total events</span>
        </div>

        {loading ? (
          <div style={{ padding:48, textAlign:"center", color:"#94a3b8" }}>Loading activity data...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding:48, textAlign:"center", color:"#94a3b8" }}>No activities found</div>
        ) : (
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
              <thead>
                <tr style={{ background:"#f8fafc" }}>
                  {["Time","Event","User","Phone","Details","Amount","APP"].map(h=>(
                    <th key={h} style={{ padding:"10px 14px", textAlign:"left", fontWeight:600, color:"#64748b", fontSize:11, textTransform:"uppercase", letterSpacing:.5, whiteSpace:"nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0,100).map((a,i)=>{
                  const ti = typeInfo(a.type);
                  return (
                    <tr key={a.id} style={{ borderTop:"1px solid #f1f5f9", background:i%2?"#fafafa":"white" }}>
                      <td style={{ padding:"10px 14px", whiteSpace:"nowrap" }}>
                        <div style={{ color:"#1e293b", fontSize:12, fontWeight:600 }}>{fmtTime(a.time)}</div>
                        <div style={{ color:"#94a3b8", fontSize:11 }}>{fmtDate(a.time)}</div>
                      </td>
                      <td style={{ padding:"10px 14px" }}>
                        <span style={{ background:ti.bg, color:ti.color, borderRadius:5, padding:"3px 10px", fontSize:11, fontWeight:700, whiteSpace:"nowrap", display:"inline-flex", alignItems:"center", gap:5 }}>
                          <span style={{ fontSize:13 }}>{ti.icon}</span> {ti.label}
                        </span>
                      </td>
                      <td style={{ padding:"10px 14px" }}>
                        <div style={{ fontWeight:600, color:"#1e293b" }}>{a.user||"—"}</div>
                        <div style={{ fontSize:11, color:"#94a3b8" }}>{a.email||""}</div>
                      </td>
                      <td style={{ padding:"10px 14px", fontFamily:"monospace", color:"#334155", whiteSpace:"nowrap" }}>{a.phone||"—"}</td>
                      <td style={{ padding:"10px 14px", color:"#64748b", maxWidth:220, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{a.detail||"—"}</td>
                      <td style={{ padding:"10px 14px", fontWeight:700, color:"#10b981", whiteSpace:"nowrap" }}>
                        {a.amount ? fmt(a.amount) : "—"}
                      </td>
                      <td style={{ padding:"10px 14px", color:"#0066cc", fontWeight:700 }}>
                        {a.appId ? `APP-${a.appId}` : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity:1; }
          50% { opacity:0.4; }
        }
      `}</style>
    </div>
  );
}
